import React, { useState } from 'react';
import { Check, Plug, RotateCcw, X } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { Z_PA_SHELL_DROPDOWN } from '@/constants/zLayers';
import { usePaConnectors } from './useConnectors';

// Task #3818 — Menu connettori nel composer: spegne/riaccende i connettori
// della chat CORRENTE, sempre e solo DENTRO il perimetro effettivo deciso
// dall'admin (il server rifiuta qualsiasi id fuori perimetro). Badge viola =
// restrizione attiva in questa conversazione.

interface Props {
  assistantId: string;
  conversationId: string | null;
  disabled?: boolean;
}

export const ConnectorsMenu: React.FC<Props> = ({ assistantId, conversationId, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const { connectors, enabled, saving, error, isActive, restrictedCount, toggle, reset } =
    usePaConnectors(assistantId, conversationId);

  const canRestrict = !!conversationId;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`relative h-9 w-9 p-0 shrink-0 ${restrictedCount > 0 ? 'text-ai' : 'text-muted-foreground hover:text-foreground'}`}
          disabled={disabled}
          title={canRestrict ? 'Connettori attivi in questa chat' : 'Connettori del perimetro (invia il primo messaggio per restringerli in chat)'}
        >
          <Plug className="h-4 w-4" />
          {restrictedCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-ai text-ai-foreground text-[9px] font-bold flex items-center justify-center">
              {restrictedCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" style={{ zIndex: Z_PA_SHELL_DROPDOWN }} className="w-72 max-h-[50vh] overflow-y-auto">
        <DropdownMenuLabel className="text-[11px] text-muted-foreground">
          Connettori · questa conversazione
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {error && (
          <div className="px-2 py-1 text-[10px] text-danger">{error}</div>
        )}
        {connectors.length === 0 && !error && (
          <div className="px-2 py-2 text-[11px] text-muted-foreground">
            Nessun connettore nel tuo perimetro.
          </div>
        )}
        {connectors.map((c) => {
          const active = isActive(c.id);
          return (
            <DropdownMenuItem
              key={c.id}
              disabled={!canRestrict || saving}
              onSelect={(e) => { e.preventDefault(); if (canRestrict) void toggle(c.id); }}
              title={c.description ?? undefined}
              className="flex items-center gap-2 py-1.5"
            >
              <span className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center ${active ? 'bg-ai border-ai' : 'border-border bg-card'}`}>
                {active ? <Check className="h-3 w-3 text-white" /> : <X className="h-3 w-3 text-muted-foreground/50" />}
              </span>
              <span className={`flex-1 truncate text-[12px] ${active ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                {c.label}
              </span>
              <span className="text-[10px] text-muted-foreground shrink-0">{c.tools.length}</span>
            </DropdownMenuItem>
          );
        })}
        {canRestrict && enabled !== null && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={saving}
              onSelect={(e) => { e.preventDefault(); void reset(); }}
              className="flex items-center gap-2 py-1.5 text-[12px] text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Riattiva tutti (default)
            </DropdownMenuItem>
          </>
        )}
        {!canRestrict && connectors.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-[10px] text-muted-foreground">
              Invia il primo messaggio per restringere i connettori di questa chat.
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
